import { useCallback, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { facilitiesLegendItemsAtom, wellsLegendItemsAtom } from "atoms";
import { IS_AWS_ENV } from "constants/app.constants";
import { useAtomValue } from "jotai";
import {
  addManyPropertiesToFilter as addManyFacilityPropertiesToFilter,
  removeAllProperties as removeAllFacilityProperties,
  setFilterState as setFacilityFilterState,
  setFiltersLoaded as setFacilityFiltersLoaded,
  setResetPolygonFilter as setFacilityResetPolygonFilter,
  setSavedFilters as setSavedFacilityFilters,
  setSelectedSavedFilter as setSelectedFacilitySavedFilter,
  toggleShowGroupsNotInFilter as toggleFacilityShowGroupsNotInFilter,
  updateActiveFilterCount as updateFacilityActiveFilterCount,
  updateShowGroupsNotInFilter as updateFacilityShowGroupsNotInFilter
} from "store/features/filter/facilityFilterSlice";
import {
  addManyPropertiesToFilter,
  removeAllProperties,
  setFilterState,
  setFiltersLoaded,
  setResetPolygonFilter,
  setSavedFilters,
  setSelectedSavedFilter,
  toggleShowGroupsNotInFilter,
  updateActiveFilterCount,
  updateShowGroupsNotInFilter
} from "store/features/filter/filterSlice";
import {
  setHasCheckedBins,
  setHasCheckedBinsFacility,
  setSelectedFacilityGroups,
  setSelectedGroups
} from "store/features/groupBy/groupBySlice";
import {
  setActiveFacilityColorPalette,
  setActiveColorPalette as setWellActiveColorPalette
} from "store/features/userSettings/userSettingsSlice";
import { RootState } from "store/rootReducer";
import { selectCheckedGlobalForecasts, useForecastStore } from "stores/forecast";
import { selectViewLock, useMapStore } from "stores/map";

import { useColumnsDictionary } from "hooks";

import { SavedFilterModel } from "models";
import { IColorPalette } from "models/colorPalette";
import { EntityKind } from "models/entityKind";
import { IFilterState, IPropertyLegendFilter } from "models/filter";

import { GroupByFilterDependencies } from "../types";

// EVA-257: the AWS build ships without HZ with Production, so a well has one
// system filter there rather than two. This count is how the controller tells
// a system filter from a user one -- it indexes past it to find the first user
// filter and to decide whether a default is available -- so leaving it at 2
// would have the AWS build read the first user filter as a system filter.
// Facility is untouched: the ticket names only the two well filters.
const NUMBER_OF_WELL_SYSTEM_FILTERS = IS_AWS_ENV ? 1 : 2;
const NUMBER_OF_FACILITY_SYSTEM_FILTERS = 1;

// EVA-6412 SR-001 — the SINGLE Well/Facility resolution point (fixes P2). Every
// downstream layer consumes this and never re-branches on entityKind (SC-003).
const selectDeps = (state: RootState, isWell: boolean) => {
  const filterSlice = isWell ? state.filter : state.facilityFilter;
  return {
    filterId: filterSlice.filterId,
    isFiltering: filterSlice.isFiltering,
    polygonFilter: filterSlice.polygonFilter,
    excludePolygonsFilter: filterSlice.excludePolygonsFilter,
    currentFilter: filterSlice.currentFilter,
    propertiesFilter: filterSlice.propertiesFilter,
    selectedFilter: filterSlice.selectedSavedFilter,
    wellListFilter: filterSlice.wellListFilter,
    showGroupsNotInFilter: filterSlice.showGroupsNotInFilter,
    savedFilters: filterSlice.savedFilters,
    // Tradeoff: the whole filter slice is carried so downstream hooks (e.g. the
    // legend recompute) can react to any filter change. It's cheap today
    // because `deps` is memoized with shallowEqual, but adding fields to the
    // slice will re-invoke all consumers on unrelated changes — narrow this to
    // the specific fields needed if the slice grows.
    fullFilterState: filterSlice,
    groupBy: isWell ? state.groupBy.globalGroupBy : state.groupBy.globalFacilityFocus,
    categoryList: isWell ? state.groupBy.categoryList : state.groupBy.facilityFields,
    placeholderBinsEnabled: state.groupBy.placeholderBinsEnabled,
    normalizeBy: state.normalizeBy.globalNormalizeBy,
    useNormalizeBy: state.normalizeBy.useNormalizeBy,
    updatePlayLegend: state.filter.updatePlayLegend,
    selectedProjectTransaction: state.project.selectedProjectTransaction,
    specialForecastFolders: state.app.userForecastSettings.specialForecastFolders,
    forecastReserveCategory: state.app.userForecastSettings.reserveCategory,
    syncWells: state.app.syncWells,
    activePalette: isWell
      ? state.userSetting.activeColorPalette
      : state.userSetting.activeFacilityColorPalette
  };
};

export const useGroupByFilterDependencies = (
  entityKind: EntityKind
): GroupByFilterDependencies => {
  const dispatch = useDispatch();
  const isWell = entityKind === EntityKind.Well;

  const redux = useSelector(
    (state: RootState) => selectDeps(state, isWell),
    shallowEqual
  );

  const columnsDictionary = useColumnsDictionary(entityKind);
  const checkedGlobalForecasts = useForecastStore(selectCheckedGlobalForecasts);
  const mapViewLocked = useMapStore(selectViewLock);
  const setViewLock = useMapStore((s) => s.setViewLock);

  const wellsLegendItems = useAtomValue(wellsLegendItemsAtom);
  const facilitiesLegendItems = useAtomValue(facilitiesLegendItemsAtom);
  const activeLegendItems = isWell ? wellsLegendItems : facilitiesLegendItems;

  const systemFilterCount = isWell
    ? NUMBER_OF_WELL_SYSTEM_FILTERS
    : NUMBER_OF_FACILITY_SYSTEM_FILTERS;

  const setSavedEntityFilters = useCallback(
    (filters: SavedFilterModel[]) => {
      dispatch(isWell ? setSavedFilters(filters) : setSavedFacilityFilters(filters));
    },
    [dispatch, isWell]
  );

  const setEntityFiltersLoaded = useCallback(
    (loaded: boolean) => {
      dispatch(isWell ? setFiltersLoaded(loaded) : setFacilityFiltersLoaded(loaded));
    },
    [dispatch, isWell]
  );

  const dispatchToggleShowGroupsNotInFilter = useCallback(() => {
    dispatch(
      isWell ? toggleShowGroupsNotInFilter() : toggleFacilityShowGroupsNotInFilter()
    );
  }, [dispatch, isWell]);

  const dispatchUpdateShowGroupsNotInFilter = useCallback(
    (value: boolean) => {
      dispatch(
        isWell
          ? updateShowGroupsNotInFilter(value)
          : updateFacilityShowGroupsNotInFilter(value)
      );
    },
    [dispatch, isWell]
  );

  const dispatchActiveFilterCount = useCallback(
    (filterId: string) => {
      dispatch(
        isWell
          ? updateActiveFilterCount(filterId)
          : updateFacilityActiveFilterCount(filterId)
      );
    },
    [dispatch, isWell]
  );

  const dispatchResetPolygonFilter = useCallback(
    (reset: boolean) => {
      dispatch(
        isWell ? setResetPolygonFilter(reset) : setFacilityResetPolygonFilter(reset)
      );
    },
    [dispatch, isWell]
  );

  const clearAllPropertiesFilter = useCallback(() => {
    dispatch(isWell ? removeAllProperties() : removeAllFacilityProperties());
    dispatchResetPolygonFilter(true);
  }, [dispatch, isWell, dispatchResetPolygonFilter]);

  const setSelectedFilter = useCallback(
    (filter: Maybe<SavedFilterModel>) => {
      dispatch(
        isWell ? setSelectedSavedFilter(filter) : setSelectedFacilitySavedFilter(filter)
      );
    },
    [dispatch, isWell]
  );

  const setFilterStateWrapper = useCallback(
    (filter: IFilterState) => {
      dispatch(isWell ? setFilterState(filter) : setFacilityFilterState(filter));
    },
    [dispatch, isWell]
  );

  const setActiveColorPalette = useCallback(
    (p: IColorPalette) => {
      dispatch(isWell ? setWellActiveColorPalette(p) : setActiveFacilityColorPalette(p));
    },
    [dispatch, isWell]
  );

  const setSelectedGroupsWrapper = useCallback(
    (titles: string[]) => {
      dispatch(isWell ? setSelectedGroups(titles) : setSelectedFacilityGroups(titles));
    },
    [dispatch, isWell]
  );

  const setHasCheckedBinsWrapper = useCallback(
    (value: boolean) => {
      dispatch(isWell ? setHasCheckedBins(value) : setHasCheckedBinsFacility(value));
    },
    [dispatch, isWell]
  );

  const addManyPropertiesToFilterWrapper = useCallback(
    (payload: IPropertyLegendFilter) => {
      dispatch(
        isWell
          ? addManyPropertiesToFilter(payload)
          : addManyFacilityPropertiesToFilter(payload)
      );
    },
    [dispatch, isWell]
  );

  return useMemo<GroupByFilterDependencies>(
    () => ({
      isWell,
      systemFilterCount,
      ...redux,
      columnsDictionary,
      checkedGlobalForecasts,
      mapViewLocked,
      setViewLock,
      activeLegendItems,
      setSavedEntityFilters,
      setEntityFiltersLoaded,
      dispatchToggleShowGroupsNotInFilter,
      dispatchUpdateShowGroupsNotInFilter,
      dispatchActiveFilterCount,
      dispatchResetPolygonFilter,
      clearAllPropertiesFilter,
      setSelectedFilter,
      setFilterState: setFilterStateWrapper,
      setActiveColorPalette,
      setSelectedGroups: setSelectedGroupsWrapper,
      setHasCheckedBins: setHasCheckedBinsWrapper,
      addManyPropertiesToFilter: addManyPropertiesToFilterWrapper
    }),
    [
      isWell,
      systemFilterCount,
      redux,
      columnsDictionary,
      checkedGlobalForecasts,
      mapViewLocked,
      setViewLock,
      activeLegendItems,
      setSavedEntityFilters,
      setEntityFiltersLoaded,
      dispatchToggleShowGroupsNotInFilter,
      dispatchUpdateShowGroupsNotInFilter,
      dispatchActiveFilterCount,
      dispatchResetPolygonFilter,
      clearAllPropertiesFilter,
      setSelectedFilter,
      setFilterStateWrapper,
      setActiveColorPalette,
      setSelectedGroupsWrapper,
      setHasCheckedBinsWrapper,
      addManyPropertiesToFilterWrapper
    ]
  );
};
